import { MapPin, Navigation, Star, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../hooks/useServices';
import { GoogleMap, mapsSearchUrl } from './GoogleMap';

export const BUSINESS_ADDRESS = 'Zur Neuen Brücke 4, 01109 Dresden';
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(BUSINESS_ADDRESS)}`;
const ext = { target: '_blank', rel: 'noopener noreferrer' };

// Until a Google Business Profile exists, links fall back to Google Maps; the review button needs a stored link.
export function useBusinessLinks() {
  const settings = useSettings();
  return {
    profileUrl: settings.google_business_url || mapsSearchUrl(BUSINESS_ADDRESS),
    hasProfile: Boolean(settings.google_business_url),
    reviewUrl: settings.google_review_url || '',
  };
}

export function GoogleBusinessSection() {
  const { t } = useLanguage();
  const { profileUrl, hasProfile, reviewUrl } = useBusinessLinks();
  const btn = 'inline-flex items-center gap-2 h-11 px-5 rounded-lg text-sm font-semibold transition-colors';

  return (
    <section className="py-20 md:py-28 bg-white border-t border-slate-100" data-testid="google-business-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-2">
          <span className="inline-block text-[#066ce7] text-sm font-semibold uppercase tracking-wider mb-3">{t('google.tag')}</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">{t('google.title')}</h2>
          <p className="text-slate-600 leading-relaxed mb-6">{t('google.text')}</p>
          <address className="not-italic flex items-start gap-3 text-[#0F172A] mb-8" data-testid="google-business-address">
            <MapPin className="w-5 h-5 text-[#066ce7] shrink-0 mt-0.5" aria-hidden="true" />
            <span>ITCS Industrial Trade & Construction Systems GmbH<br />Zur Neuen Brücke 4<br />01109 Dresden</span>
          </address>
          <div className="flex flex-wrap gap-3">
            <a href={profileUrl} {...ext} className={`${btn} bg-[#066ce7] hover:bg-[#005bb5] text-white`} data-testid="google-profile-link">
              {t(hasProfile ? 'google.profile' : 'google.maps')} <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </a>
            <a href={DIRECTIONS_URL} {...ext} className={`${btn} border border-slate-300 text-[#0F172A] hover:border-[#066ce7] hover:text-[#066ce7]`} data-testid="google-directions-link">
              <Navigation className="w-4 h-4" aria-hidden="true" /> {t('google.directions')}
            </a>
            {reviewUrl && (
              <a href={reviewUrl} {...ext} className={`${btn} border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100`} data-testid="google-review-btn">
                <Star className="w-4 h-4 fill-amber-400 text-amber-500" aria-hidden="true" /> {t('google.review')}
              </a>
            )}
          </div>
        </div>
        <GoogleMap query={BUSINESS_ADDRESS} title="ITCS Industrial – Standort Dresden" className="lg:col-span-3 h-[340px] md:h-[420px]" testid="contact-map" />
      </div>
    </section>
  );
}

export function FooterGoogleLinks() {
  const { t } = useLanguage();
  const { profileUrl, hasProfile, reviewUrl } = useBusinessLinks();
  const pill = 'inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 transition-colors';

  return (
    <div className="flex flex-wrap gap-2 pt-1" data-testid="footer-google">
      <a href={profileUrl} {...ext} className={`${pill} border border-slate-700 text-slate-300 hover:border-[#3b8dff] hover:text-white`} data-testid="footer-google-profile">
        <MapPin className="w-3.5 h-3.5" aria-hidden="true" /> {t(hasProfile ? 'google.profile' : 'google.maps')}
      </a>
      {reviewUrl && (
        <a href={reviewUrl} {...ext} className={`${pill} bg-amber-400/10 border border-amber-400/40 text-amber-300 hover:bg-amber-400/20`} data-testid="footer-google-review">
          <Star className="w-3.5 h-3.5 fill-amber-400" aria-hidden="true" /> {t('google.review')}
        </a>
      )}
    </div>
  );
}
